import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { workingCalendarApi } from '../../api/workingCalendar';
import { getDcs } from '../../../reference/api/catalog';
import {
  getCurrentPersianDate,
  PERSIAN_MONTHS,
} from '../../../../shared/utils/persianDate';
import {
  GREGORIAN_MONTHS,
  getCurrentGregorianDate,
  normalizeCalendarType,
} from '../../../../shared/utils/calendarType';
import { Button, LoadingBlock } from '../../../../shared/ui/composites';
import { cn } from '../../../../lib/utils';

const WEEKDAYS = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه'];

function shiftMonth(year, month, delta) {
  let m = month + delta;
  let y = year;
  while (m < 1) {
    m += 12;
    y -= 1;
  }
  while (m > 12) {
    m -= 12;
    y += 1;
  }
  return { year: y, month: m };
}

export default function WorkingCalendarPage({ calendarType = 'jalali' }) {
  const calendar = normalizeCalendarType(calendarType);
  const isGregorian = calendar === 'gregorian';
  const today = isGregorian ? getCurrentGregorianDate() : getCurrentPersianDate();
  const [year, setYear] = useState(today.year);
  const [month, setMonth] = useState(today.month);
  const [dcs, setDcs] = useState([]);
  const [dcId, setDcId] = useState('');
  const [dcName, setDcName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [dirty, setDirty] = useState(false);

  /** Map day_number → note */
  const [holidayMap, setHolidayMap] = useState(() => new Map());
  const [meta, setMeta] = useState(null);
  const [baseline, setBaseline] = useState(null);

  const monthNames = isGregorian ? GREGORIAN_MONTHS : PERSIAN_MONTHS;
  const monthLabel = monthNames[month - 1] || month;

  const loadDcs = useCallback(async () => {
    try {
      const list = await getDcs();
      const arr = Array.isArray(list) ? list : [];
      setDcs(arr);
      if (!dcId && arr.length) {
        setDcId(String(arr[0].dc_id));
        setDcName(arr[0].dc_name || '');
      }
    } catch {
      setDcs([]);
    }
  }, [dcId]);

  const applyMonthPayload = (data) => {
    const map = new Map();
    (data.holidays || []).forEach((h) => {
      map.set(Number(h.day_number), h.note || '');
    });
    setHolidayMap(map);
    setMeta(data);
    setBaseline(JSON.stringify([...map.entries()].sort((a, b) => a[0] - b[0])));
    setDirty(false);
  };

  const loadMonth = useCallback(async () => {
    if (!dcId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const { data } = await workingCalendarApi.getMonth({
        dc_id: dcId,
        year,
        month,
        dc_name: dcName || undefined,
        calendar_type: calendar,
      });
      applyMonthPayload(data);
    } catch (err) {
      setMessage(err?.response?.data?.error || err.message || 'خطا در بارگذاری');
      setHolidayMap(new Map());
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [dcId, year, month, dcName, calendar]);

  useEffect(() => {
    const next = isGregorian ? getCurrentGregorianDate() : getCurrentPersianDate();
    setYear(next.year);
    setMonth(next.month);
  }, [isGregorian]);

  useEffect(() => {
    loadDcs();
  }, [loadDcs]);

  useEffect(() => {
    loadMonth();
  }, [loadMonth]);

  const markDirty = (nextMap) => {
    setHolidayMap(nextMap);
    const serialized = JSON.stringify([...nextMap.entries()].sort((a, b) => a[0] - b[0]));
    setDirty(serialized !== baseline);
  };

  const toggleDay = (day) => {
    const next = new Map(holidayMap);
    if (next.has(day)) next.delete(day);
    else next.set(day, '');
    markDirty(next);
  };

  const setNote = (day, note) => {
    if (!holidayMap.has(day)) return;
    const next = new Map(holidayMap);
    next.set(day, note);
    markDirty(next);
  };

  const removeHoliday = (day) => {
    const next = new Map(holidayMap);
    next.delete(day);
    markDirty(next);
  };

  const handleReset = () => {
    if (baseline == null) return;
    const entries = JSON.parse(baseline);
    markDirty(new Map(entries));
    setMessage('');
  };

  const handleSave = async () => {
    if (!dcId) return;
    setSaving(true);
    setMessage('');
    try {
      const holidays = [...holidayMap.entries()].map(([day_number, note]) => ({
        day_number,
        note,
      }));
      const { data } = await workingCalendarApi.saveMonth({
        dc_id: Number(dcId),
        dc_name: dcName || null,
        year_number: year,
        month_number: month,
        calendar_type: calendar,
        holidays,
      });
      applyMonthPayload(data);
      setMessage('ذخیره با موفقیت انجام شد');
    } catch (err) {
      setMessage(err?.response?.data?.error || err.message || 'خطا در ذخیره');
    } finally {
      setSaving(false);
    }
  };

  const onDcChange = (id) => {
    const dc = dcs.find((d) => String(d.dc_id) === String(id));
    setDcId(String(id));
    setDcName(dc?.dc_name || '');
  };

  const daysInMonth = meta?.days_in_month || 31;
  const startWeekday = meta?.start_weekday ?? 0;
  const todayDay = meta?.today_day;

  const livePace = useMemo(() => {
    const holidays = [...holidayMap.keys()];
    const holidayCount = holidays.length;
    const workingDays = daysInMonth - holidayCount;
    const asOf = todayDay != null ? todayDay : null;
    const holidaysToDate =
      asOf == null ? null : holidays.filter((d) => d <= asOf).length;
    const current =
      asOf == null ? null : Math.max(asOf - (holidaysToDate || 0), 0);
    const remaining =
      current == null ? null : Math.max(workingDays - current, 0);
    return {
      holiday_count: holidayCount,
      working_days: workingDays,
      holidays_to_date: holidaysToDate,
      current_working_day: current,
      remaining_working_days: remaining,
    };
  }, [holidayMap, daysInMonth, todayDay]);

  const selectedHolidays = useMemo(
    () =>
      [...holidayMap.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([day, note]) => ({ day, note })),
    [holidayMap],
  );

  const cells = useMemo(() => {
    const list = [];
    for (let i = 0; i < startWeekday; i += 1) {
      list.push({ type: 'pad', key: `pad-${i}` });
    }
    for (let d = 1; d <= daysInMonth; d += 1) {
      list.push({ type: 'day', day: d, key: `d-${d}` });
    }
    while (list.length % 7 !== 0) {
      list.push({ type: 'pad', key: `pad-end-${list.length}` });
    }
    return list;
  }, [startWeekday, daysInMonth]);

  return (
    <div className="rtl w-full" dir="rtl">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xl" aria-hidden>📅</span>
          <h2 className="text-lg font-semibold text-slate-800">
            {isGregorian ? 'تعطیلات ماهانه میلادی' : 'تعطیلات ماهانه شمسی'}
          </h2>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <span className="text-xs font-bold text-slate-400">مرکز / شعبه</span>
            <select
              className="min-w-[160px] rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm"
              value={dcId}
              onChange={(e) => onDcChange(e.target.value)}
            >
              {!dcs.length && <option value="">مرکزی نیست</option>}
              {dcs.map((d) => (
                <option key={d.dc_id} value={d.dc_id}>
                  {d.dc_name || d.dc_id}
                </option>
              ))}
            </select>
          </label>

          <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-1 py-0.5">
            <button
              type="button"
              className="rounded px-2 py-1 text-slate-500 hover:bg-slate-50"
              onClick={() => {
                const n = shiftMonth(year, month, -1);
                setYear(n.year);
                setMonth(n.month);
              }}
              aria-label="ماه قبل"
            >
              ‹
            </button>
            <span className="min-w-[110px] text-center text-sm font-semibold text-slate-700">
              {monthLabel} / {year}
            </span>
            <button
              type="button"
              className="rounded px-2 py-1 text-slate-500 hover:bg-slate-50"
              onClick={() => {
                const n = shiftMonth(year, month, 1);
                setYear(n.year);
                setMonth(n.month);
              }}
              aria-label="ماه بعد"
            >
              ›
            </button>
          </div>

          <Button type="button" variant="secondary" size="sm" onClick={handleReset} disabled={!dirty || saving}>
            بازنشانی
          </Button>
          <Button type="button" variant="submit" size="sm" onClick={handleSave} disabled={!dirty || saving || !dcId}>
            {saving ? '…' : 'ذخیره تغییرات'}
          </Button>
        </div>
      </div>

      {message && (
        <p
          className={cn(
            'mb-3 text-sm',
            message.includes('موفق') ? 'text-emerald-600' : 'text-rose-600',
          )}
        >
          {message}
        </p>
      )}

      {!dcId ? (
        <p className="text-sm text-slate-500">ابتدا یک مرکز توزیع انتخاب کنید. (جدول فروش باید DC داشته باشد)</p>
      ) : loading ? (
        <LoadingBlock />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">
          {/* Calendar */}
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50">
              {WEEKDAYS.map((w) => (
                <div key={w} className="px-1 py-2 text-center text-[11px] font-bold text-slate-500">
                  {w}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {cells.map((cell) => {
                if (cell.type === 'pad') {
                  return <div key={cell.key} className="min-h-[76px] border border-slate-50 bg-slate-50/40" />;
                }
                const isHoliday = holidayMap.has(cell.day);
                const isToday = todayDay === cell.day;
                return (
                  <button
                    key={cell.key}
                    type="button"
                    onClick={() => toggleDay(cell.day)}
                    className={cn(
                      'relative flex min-h-[76px] flex-col items-start gap-1 border border-slate-100 p-2 text-right transition',
                      isHoliday
                        ? 'bg-rose-50 hover:bg-rose-100/80'
                        : 'bg-white hover:bg-slate-50',
                      isToday && 'ring-2 ring-inset ring-amber-400',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-5 w-5 items-center justify-center rounded border text-[10px]',
                        isHoliday
                          ? 'border-rose-400 bg-rose-500 text-white'
                          : 'border-slate-300 bg-white text-transparent',
                      )}
                      aria-hidden
                    >
                      {isHoliday ? '✓' : ''}
                    </span>
                    <span
                      className={cn(
                        'text-lg font-bold leading-none',
                        isHoliday ? 'text-rose-600' : 'text-slate-800',
                      )}
                    >
                      {cell.day}
                    </span>
                    <span className={cn('text-[10px]', isHoliday ? 'text-rose-400' : 'text-slate-400')}>
                      {monthLabel}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Side panel */}
          <div className="flex flex-col gap-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
              <h3 className="mb-2 text-sm font-bold text-slate-700">تعطیلات انتخاب‌شده</h3>
              {selectedHolidays.length === 0 ? (
                <p className="text-xs text-slate-400">روی روزها کلیک کنید تا تعطیل شوند.</p>
              ) : (
                <ul className="max-h-[280px] space-y-2 overflow-y-auto">
                  {selectedHolidays.map(({ day, note }) => (
                    <li key={day} className="flex items-start gap-2 rounded-lg bg-rose-50/80 p-2">
                      <button
                        type="button"
                        className="mt-0.5 text-rose-500 hover:text-rose-700"
                        onClick={() => removeHoliday(day)}
                        title="حذف"
                      >
                        ×
                      </button>
                      <div className="min-w-0 flex-1">
                        <input
                          className="mb-1 w-full rounded border border-rose-100 bg-white px-2 py-1 text-xs text-slate-700"
                          placeholder="توضیح / مناسبت"
                          value={note}
                          onChange={(e) => setNote(day, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="inline-block rounded-full bg-rose-200/80 px-2 py-0.5 text-[10px] font-bold text-rose-700">
                          {day} {monthLabel}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
              <h3 className="mb-2 text-sm font-bold text-slate-700">خلاصه ماه (مثل شیت اکسل)</h3>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-xl bg-emerald-50 p-3 text-center">
                  <div className="text-[11px] font-bold text-emerald-700">روز کاری</div>
                  <div className="text-xl font-black text-emerald-800">{livePace.working_days}</div>
                </div>
                <div className="rounded-xl bg-amber-50 p-3 text-center">
                  <div className="text-[11px] font-bold text-amber-700">روز جاری</div>
                  <div className="text-xl font-black text-amber-800">
                    {livePace.current_working_day ?? '—'}
                  </div>
                </div>
                <div className="rounded-xl bg-sky-50 p-3 text-center">
                  <div className="text-[11px] font-bold text-sky-700">روز باقی مانده</div>
                  <div className="text-xl font-black text-sky-800">
                    {livePace.remaining_working_days ?? '—'}
                  </div>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div className="rounded-xl bg-rose-50 p-2 text-center">
                  <div className="text-[10px] font-bold text-rose-700">روز تعطیل</div>
                  <div className="text-lg font-black text-rose-800">{livePace.holiday_count}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-2 text-center">
                  <div className="text-[10px] font-bold text-slate-500">روز تقویم ماه</div>
                  <div className="text-lg font-black text-slate-700">{daysInMonth}</div>
                </div>
              </div>
              {todayDay != null ? (
                <div className="mt-2 space-y-1 rounded-xl bg-slate-50 p-2 text-[11px] text-slate-600">
                  <div>
                    امروز: روز {todayDay} از تقویم — تعطیل تا امروز: {livePace.holidays_to_date}
                  </div>
                  <div className="font-semibold text-indigo-700">
                    کانتر: روز {livePace.current_working_day} از {livePace.working_days} روز کاری
                  </div>
                  <div className="text-slate-400">
                    فرمول: روز جاری = {todayDay} − {livePace.holidays_to_date} ؛ باقی‌مانده = روز کاری − روز جاری
                  </div>
                </div>
              ) : (
                <p className="mt-2 text-[11px] text-slate-400">
                  برای ماه‌های غیر جاری، کانتر «روز جاری / باقی‌مانده» فقط پس از انتخاب ماه جاری یا با پارامتر{' '}
                  <code dir="ltr">as_of_day</code> در گزارش محاسبه می‌شود.
                </p>
              )}
              <p className="mt-2 text-[10px] leading-relaxed text-slate-400">
                تعطیلات هر شعبه جداست (مثلاً فارس ۲۴ و تهران ۲۲ روز کاری). در گزارش‌ساز از{' '}
                <code dir="ltr">fn_dc_working_pace(:dc_id, :year, :month, :as_of_day)</code> و نمونه{' '}
                <code dir="ltr">sample-pace-report.sql</code> استفاده کنید.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
